import { useState } from 'react'
import { StepContainer } from '../components/StepContainer'
import { AlreadySubscribed } from '../components/AlreadySubscribed'
import { useOnboardingStore } from '../store/onboardingStore'
import { trackEmailEntered, trackSubscriptionBlockShown, trackPaidSetupResumed, trackMetaLead, fireMetaEmailSubmitted } from '../lib/analytics'
import { createLoopsContact } from '../lib/loops'
import { getLoopsSource } from '../lib/variant'
import { calculateNutrientLimits } from '../lib/nutrients'
import { checkExistingSubscription, type CheckResult } from '../lib/checkSubscription'

export function EmailStep() {
  const { data, goNext, goToStep } = useOnboardingStore()
  const [email, setEmail] = useState(data.email || '')
  const [consent, setConsent] = useState(data.marketingConsent)
  const [checking, setChecking] = useState(false)
  const [existing, setExisting] = useState<CheckResult | null>(null)

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const handleContinue = async () => {
    if (!isValid || checking) return

    setChecking(true)
    const result = await checkExistingSubscription(email)
    setChecking(false)

    if (result.alreadySubscribed) {
      trackSubscriptionBlockShown(result.platform)
      setExisting(result)
      return
    }

    // Paid here before but never finished the account: no second offer — go
    // straight to account setup, which claims the waiting purchase.
    if (result.pendingWebPurchase) {
      trackPaidSetupResumed()
      goToStep('createAccount', { email, marketingConsent: consent, paidSetup: true })
      return
    }

    trackEmailEntered()
    trackMetaLead()
    fireMetaEmailSubmitted()

    // Send to Loops for abandoned cart sequence (fire and forget)
    const limits = calculateNutrientLimits(data.kidneyStatus, data.comorbidities, data.weightKg, data.dialysisType)
    createLoopsContact({
      email,
      source: getLoopsSource(),
      kidneyStatus: data.kidneyStatus || undefined,
      primaryGoal: data.primaryGoal || undefined,
      weightKg: data.weightKg || undefined,
      sodiumLimit: limits.sodium,
      potassiumLimit: limits.potassium,
      phosphorusLimit: limits.phosphorus,
      proteinLimit: limits.protein,
      // No limit (peritoneal dialysis) = no property: the "plan ready" Loops email
      // shows its fluid card only when fluidLimitMl > 0 (fallback "0").
      fluidLimitMl: limits.fluid || undefined,
      marketingConsent: consent,
    })

    goNext({ email, marketingConsent: consent })
  }

  if (existing) {
    return (
      <AlreadySubscribed
        email={email}
        platform={existing.platform}
        onUseDifferentEmail={() => {
          setExisting(null)
          setEmail('')
        }}
      />
    )
  }

  return (
    <StepContainer
      title="Where should we send your plan?"
      subtitle="We'll personalize your kidney diet plan based on everything you've told us"
      onContinue={handleContinue}
      continueDisabled={!isValid || checking}
      continueLabel={checking ? 'Checking…' : 'Build My Plan'}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900"
            autoComplete="email"
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm text-gray-500">
            Send me kidney health tips and recipe recommendations. Unsubscribe anytime.
          </span>
        </label>

        <div className="flex items-center gap-2 text-xs text-gray-400 mt-4">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>Your email is private and will never be shared</span>
        </div>
      </div>
    </StepContainer>
  )
}
